import React, { useState, useEffect } from 'react';
import cartService, { CartItem } from '../../services/cart.service';
import socketService from '../../services/socket.service';
import checkoutStateService from '../../services/checkout-state.service';
import { Cell } from '../../ultis/types';
import './CartPopup.css';

interface CartPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onCheckoutSuccess?: (billId: string, expAt: number, qr?: string) => void;
}

const PRICE_PER_CELL = 10000; // 10.000 VND per cell

const CartPopup: React.FC<CartPopupProps> = ({ isOpen, onClose, onCheckoutSuccess }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [errorCells, setErrorCells] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (isOpen) {
      // Load cart items when popup opens
      setCartItems(cartService.getCart());
      setErrorCells(new Set()); // Clear errors when popup opens
    }
  }, [isOpen]);

  // Setup checkout response listener when popup is open
  useEffect(() => {
    if (!isOpen) return;

    // Listen for checkout response
    const handleCheckoutResponse = (response: any) => {
      console.log('[CartPopup] Received checkout response:', response);
      setIsCheckoutLoading(false);
      
      if (response.success && response.billId && response.expAt) {
        // Success - close popup and open payment popup
        console.log('[CHECKOUT] Success! Bill ID:', response.billId, 'ExpAt:', response.expAt, 'QR:', response.qr);
        setErrorCells(new Set());
        
        // Clear cart after successful checkout
        cartService.clearCart();
        
        // Close cart popup and open payment popup
        if (onCheckoutSuccess) {
          onCheckoutSuccess(response.billId, response.expAt, response.qr);
        }
        onClose();
      } else if (response.errorCells && response.errorCells.length > 0) {
        // Error - show error cells
        const errorCellIndices = new Set<number>(
          response.errorCells
            .filter((cell: any) => cell.i !== undefined && typeof cell.i === 'number')
            .map((cell: any) => cell.i as number)
        );
        setErrorCells(errorCellIndices);
        
        // Log errors
        console.error('[CHECKOUT] Errors:', response.errorCells);
        response.errorCells.forEach((errorCell: any) => {
          console.error(`  - Ô ${errorCell.i}: ${errorCell.reason}`);
        });
      } else {
        // Unexpected response format
        console.warn('[CHECKOUT] Unexpected response format:', response);
      }
    };

    console.log('[CartPopup] Setting up checkout response listener (popup is open)');
    socketService.onCheckoutResponse(handleCheckoutResponse);

    return () => {
      console.log('[CartPopup] Cleaning up checkout response listener (popup closed)');
      socketService.offCheckoutResponse(handleCheckoutResponse);
    };
  }, [isOpen]);

  useEffect(() => {
    // Listen for cart updates
    const handleCartUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.cart) {
        setCartItems(customEvent.detail.cart);
      } else {
        setCartItems(cartService.getCart());
      }
    };

    // Listen for checkout list updates
    const handleCheckoutListUpdate = () => {
      // Kiểm tra lại các ô trong giỏ hàng có bị checkout không
      const errorCellsLocal: Set<number> = new Set();
      cartItems.forEach((item: CartItem) => {
        if (item.cell.i !== undefined) {
          if (checkoutStateService.isCellCheckedOut(item.cell.i)) {
            errorCellsLocal.add(item.cell.i);
          }
        }
      });
      
      if (errorCellsLocal.size > 0) {
        setErrorCells(errorCellsLocal);
      }
    };

    window.addEventListener('cart-updated', handleCartUpdate);
    window.addEventListener('checkout-list-updated', handleCheckoutListUpdate);

    return () => {
      window.removeEventListener('cart-updated', handleCartUpdate);
      window.removeEventListener('checkout-list-updated', handleCheckoutListUpdate);
    };
  }, [cartItems]);

  const handleRemoveItem = (cellIndex: number) => {
    cartService.removeFromCart(cellIndex);
  };

  const handleCheckout = () => {
    if (cartItems.length === 0 || isCheckoutLoading) {
      return;
    }

    // Check socket connection
    if (!socketService.isConnected()) {
      console.error('[CHECKOUT] Socket is not connected!');
      setIsCheckoutLoading(false);
      alert('Socket chưa kết nối. Vui lòng thử lại sau.');
      return;
    }

    // Clear previous errors
    setErrorCells(new Set());

    // Check checkout state trước khi gửi lên server
    const errorCellsLocal: Set<number> = new Set();
    cartItems.forEach((item: CartItem) => {
      if (item.cell.i !== undefined) {
        if (checkoutStateService.isCellCheckedOut(item.cell.i)) {
          errorCellsLocal.add(item.cell.i);
        }
      }
    });

    // Nếu có ô đã checkout, hiển thị error ngay
    if (errorCellsLocal.size > 0) {
      setErrorCells(errorCellsLocal);
      console.error('[CHECKOUT] Some cells are already checked out:', Array.from(errorCellsLocal));
      return;
    }

    // Set loading state
    setIsCheckoutLoading(true);

    // Chuẩn bị dữ liệu để gửi: chỉ gửi i, x, y của các cells
    const cells = cartItems.map((item: CartItem) => ({
      i: item.cell.i,
      x: item.cell.x,
      y: item.cell.y,
    }));

    console.log('[CHECKOUT] Sending checkout request:', cells);
    // Gửi qua socket
    socketService.checkout({ cells });
  };

  const calculateTotal = (): number => {
    return cartItems.length * PRICE_PER_CELL;
  };

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(price);
  };

  if (!isOpen) return null;

  return (
    <div className="cart-popup-overlay" onClick={onClose}>
      <div className="cart-popup" onClick={(e) => e.stopPropagation()}>
        <div className="cart-popup-header">
          <h2>Giỏ hàng của bạn</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="cart-popup-content">
          {cartItems.length === 0 ? (
            <div className="cart-empty">
              <p>Giỏ hàng của bạn đang trống</p>
            </div>
          ) : (
            <>
              <div className="cart-items-list">
                {cartItems.map((item: CartItem) => {
                  const hasError = item.cell.i !== undefined && errorCells.has(item.cell.i);
                  return (
                    <div key={item.cell.i} className={`cart-item ${hasError ? 'cart-item-error' : ''}`}>
                      <div className="cart-item-info">
                        <span className="cart-item-title">Ô {item.cell.i}</span>
                        {hasError && (
                          <span className="cart-item-error-text">Đã có lỗi</span>
                        )}
                      </div>
                      <button 
                        className="cart-item-remove"
                        onClick={() => handleRemoveItem(item.cell.i || -1)}
                      >
                        Xóa
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="cart-summary">
                <div className="cart-summary-row">
                  <span>Số lượng:</span>
                  <span>{cartItems.length} ô</span>
                </div>
                <div className="cart-summary-row total">
                  <span>Tổng tiền:</span>
                  <span>{formatPrice(calculateTotal())}</span>
                </div>
              </div>

              <button 
                className="checkout-btn" 
                onClick={handleCheckout}
                disabled={isCheckoutLoading}
              >
                {isCheckoutLoading ? 'Đang xử lý...' : 'Checkout'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartPopup;


